import * as React from 'react';
import { useState, useEffect } from 'react';
import * as Modal from 'react-modal';
import Select from 'react-select';

//Libs
import { useDispatch, useSelector } from 'react-redux';

//Components
import { File } from '../../../../modules/metajson-sub/src/libs/controls/Files/components/File/File';
import { Guid, checkFileExtension, getFiletExtension } from '../../../Utils';
import { blobToBase64 } from '../../../../modules/metajson-sub/src/libs/helpers/Utils';
import { SIGNATURE_TYPE, availableFormats } from '../const';
import { getDocumentTable } from '../actions';
import { SigCreator } from './SigCreator';
import { ModalProps, availableFormat, signType } from '../types';
import MaskInput from 'react-maskinput';

import goskey from '../../../../static/files/goskey.pdf';
import ep from '../../../../static/files/ep.pdf';

export const SignDocModal = (props: ModalProps) => {
  const { isOpen, fileTemplate, api } = props;

  const SNILS_SIZE = 11;

  const [signType, setSignType] = useState(SIGNATURE_TYPE[1]);
  const [options, setOptions] = useState(
    filterOptions(SIGNATURE_TYPE, signType),
  );
  const [errorFormat, setErrorFormat] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [gkErrorMessage, setGkErrorMessage] = useState(null);
  const [snilsNumber, setSnilsNumber] = useState(null);

  const [rowId, fileSigned, form_type, process_id] = useSelector(
    (state: any) => [
      state.SignGridComponentReducer.rowId,
      state.SignGridComponentReducer.fileSigned,
      state.forms.form_type,
      state.forms.process_id,
    ],
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: 'DELETE_FILE_SIGNED' });
    setErrorFormat(undefined);
  }, [signType]);

  function filterOptions(
    optionsArray: Array<signType>,
    optionToDelete: signType,
  ) {
    return optionsArray.filter(
      (option: signType) => option.value !== optionToDelete.value,
    );
  }

  const handleChange = (value) => {
    setSignType(value);
  };

  const onCloseModal = () => {
    dispatch({ type: 'TOGGLE_SIGN_MODAL', payload: false });
    dispatch({ type: 'SET_FILE_SIGNED', payload: null });
    setSignType(SIGNATURE_TYPE[1]);
    setSnilsNumber(null);
  };

  const onUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    const filename = files[0].name;

    const formatToCheck = availableFormats.find(
      (elem: availableFormat) => elem.signType === signType.value,
    ).formats;

    if (
      checkFileExtension(filename, formatToCheck) &&
      (signType.value === '0' || signType.value === '1')
    ) {
      setErrorFormat(null);
      blobToBase64(files[0]).then((res) => {
        dispatch({
          type: 'SET_FILE_SIGNED',
          payload: {
            file: files[0],
            name: files[0].name,
            url: res,
          },
        });
      });
    } else {
      let msg = '';

      if (!checkFileExtension(filename, formatToCheck)) {
        msg = `Неверный формат файла: ${getFiletExtension(filename)}`;
      }

      setErrorFormat(msg);
    }
  };

  const saveAndSendFile = () => {
    const form_data = new FormData();
    form_data.append('file', fileSigned.file);
    form_data.append('fileId', Guid.newGuid());
    setIsLoading(true);

    api.uploadDocumentsFile(signType.value, rowId, form_data, {
      success: ({ data }) => {
        setIsLoading(false);
        getDocumentTable(form_type, process_id)(dispatch);
        onCloseModal();
      },
      error: (error) => {
        if (typeof error.response.data.detail === 'string') {
          setErrorFormat(error.response.data.detail);
        } else {
          dispatch({ type: 'SET_FILE_SIGNED', payload: null });
          setErrorFormat('Произошла ошибка. Попробуйте еще раз.');
        }
        setIsLoading(false);
      },
    });
  };

  const sendToGK = () => {
    if (
      snilsNumber === null ||
      snilsNumber.replace(/[^0-9]/g, '').length === 0
    ) {
      setGkErrorMessage('Введите СНИЛС');
      return;
    }

    setIsLoading(true);

    api.sendFileToGK(rowId, snilsNumber, {
      success: ({ data }) => {
        setIsLoading(false);
        getDocumentTable(form_type, process_id)(dispatch);
        onCloseModal();
      },
      error: (error) => {
        if (typeof error.response.data.detail === 'string') {
          setGkErrorMessage(error.response.data.detail);
        } else {
          setGkErrorMessage('Произошла ошибка. Попробуйте еще раз.');
          setSnilsNumber(null);
        }
        setIsLoading(false);
      },
    });
  };

  const validateSnils = (value: string) => {
    const checkSum = parseInt(value.slice(9), 10);

    if (value.length === 0) {
      return;
    }

    if (value.length !== SNILS_SIZE) {
      return `Некорректная длина СНИЛС: ${value.length}.`;
    }

    const valueToArray = value.split('').map((text) => Number(text));

    const sum =
      valueToArray[0] * 9 +
      valueToArray[1] * 8 +
      valueToArray[2] * 7 +
      valueToArray[3] * 6 +
      valueToArray[4] * 5 +
      valueToArray[5] * 4 +
      valueToArray[6] * 3 +
      valueToArray[7] * 2 +
      valueToArray[8] * 1;

    if (sum < 100 && sum == checkSum) {
      return;
    } else if ((sum == 100 || sum == 101) && checkSum == 0) {
      return;
    } else if (
      sum > 101 &&
      (sum % 101 == checkSum || (sum % 101 == 100 && checkSum == 0))
    ) {
      return;
    } else {
      return 'Некорректный СНИЛС';
    }
  };

  const maskOnBlur = (e) => {
    const { value } = e.target;
    const matches = value.replace(/[^0-9]/g, '');
    const errorMessage = validateSnils(matches);

    if (errorMessage) {
      setGkErrorMessage(errorMessage);
    } else {
      setGkErrorMessage(null);
      setSnilsNumber(value);
    }
  };

  useEffect(() => {
    setOptions(filterOptions(SIGNATURE_TYPE, signType));
  }, [signType]);

  const renderFileInput = () => {
    return (
      <div className="form-group">
        <div className="col-xs-12 fullwidth">
          <label className="control-label">
            <div className="reportbox-content-desc">
              <span>
                {signType.value === '0'
                  ? 'Приложить скан-копию подписанного заявления в формате PDF'
                  : 'Приложите файл подписи в формате .sig'}
              </span>
            </div>
          </label>
        </div>
        <div className="col-xs-12" style={{ marginBottom: '20px' }}>
          <div className="input-group">
            <File
              isLoading={false}
              onChange={(e) => {
                onUpload(e);
              }}
              value={fileSigned}
            />
          </div>
          {errorFormat && (
            <div role="alert" className="alert alert-danger">
              {errorFormat}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    // @ts-ignore
    <Modal
      isOpen={isOpen}
      className="react-bs-insert-modal modal-dialog"
      contentLabel="Modal"
      ariaHideApp={false}
    >
      <div className="modal-content react-bs-table-insert-modal">
        <div className="modal-header ">
          <button type="button" className="close" onClick={onCloseModal}>
            <span aria-hidden="true">×</span>
          </button>
          {signType.value === '0' && (
            <h4 className="modal-title">Приложите подписанный скан-копию</h4>
          )}
          {signType.value === '1' && (
            <h4 className="modal-title">Приложите подписанный файл</h4>
          )}
          {signType.value === '2' && (
            <h4 className="modal-title">Подписание документа в Госключе</h4>
          )}
          {signType.value === '3' && (
            <h4 className="modal-title">Подписание документа в браузере</h4>
          )}
        </div>
        <div className="modal-body">
          <div className="form-group">
            {signType.value === '1' && (
              <div className="goskey-description">
                <p>Обращаем внимание, что необходимо приложить файл открепленной подписи к документу.
                  <a rel="noreferrer" href={ep} target="_blank">
                    Инструкция по формированию открепленной подписи
                  </a>
                </p>
              </div>
            )}
            {signType.value === '2' && (
              <div className="goskey-description">
                <p>
                  Воспользуйтесь подписанием документа «Согласие на участие в
                  проекте» члена Вашей команды в приложении «Госключ».
                </p>
                <p>
                  «Госключ» — это приложение для подписания юридически значимых
                  документов в электронном виде. Вы можете отправить документ в
                  приложение, указав СНИЛС подписанта.
                </p>
                <p>
                  Член команды проекта может бесплатно получить сертификат,
                  сформировать усиленную квалифицированную электронную подпись
                  (УКЭП) и подписать документ в приложении «Госключ».
                </p>
                <p>Для подписания ему необходимы:</p>
                <ol>
                  <li>
                    <a
                      rel="noreferrer"
                      href="https://www.gosuslugi.ru/help/faq/login/2"
                      target="_blank"
                    >
                      Подтверждённая учётная запись на Госуслугах
                    </a>
                  </li>
                  <li>
                    Телефонный номер, который указан в учётной записи, — на него
                    придёт код активации приложения «Госключ»
                  </li>
                  <li>
                    Один из способов идентификации личности:
                    <ul>
                      <li>
                        действующий загранпаспорт нового образца и телефон с NFC
                      </li>
                      <li>
                        <a
                          rel="noreferrer"
                          href="https://www.gosuslugi.ru/ebs_verification"
                          target="_blank"
                        >
                          подтверждённая биометрия
                        </a>
                      </li>
                      <li>
                        <a
                          rel="noreferrer"
                          href="https://goskey.ru/map/"
                          target="_blank"
                        >
                          очная идентификация
                        </a>
                      </li>
                    </ul>
                  </li>
                </ol>
                <p>
                  <a rel="noreferrer" href={goskey} target="_blank">
                    Полное руководство по использованию приложения «Госключ»
                  </a>
                </p>
              </div>
            )}

            <div className="form-group">
              <div className="col-xs-12 fullwidth">
                <label className="control-label">
                  <div className="reportbox-content-desc">
                    <span>Файл заявления</span>
                  </div>
                </label>
              </div>
              <div className="col-xs-12" style={{ marginBottom: '20px' }}>
                <div className="input-group">
                  <File
                    isLoading={false}
                    onChange={(e) => onUpload(e)}
                    value={fileTemplate}
                  />
                </div>
              </div>
            </div>

            {signType.value === '0' ? renderFileInput() : null}
            {signType.value === '1' ? renderFileInput() : null}
            {signType.value === '2' && (
              <div className="form-group">
                <div className="col-xs-12 fullwidth">
                  <label className="control-label">
                    <div className="reportbox-content-desc">
                      <span>СНИЛС подписанта</span>
                    </div>
                  </label>
                </div>
                <div className="col-xs-12">
                  <div className="input-group" style={{ width: '100%' }}>
                    <div style={{ width: '100%' }}>
                      <MaskInput
                        // @ts-ignore
                        type="text"
                        className="form-control"
                        inputMode="decimal"
                        mask="000-000-000 00"
                        maskChar="_"
                        size={SNILS_SIZE}
                        alwaysShowMask={true}
                        onBlur={maskOnBlur}
                        value={snilsNumber || ''}
                      />
                    </div>
                  </div>
                  {gkErrorMessage && (
                    <div role="alert" className="alert alert-danger">
                      {gkErrorMessage}
                    </div>
                  )}
                </div>
              </div>
            )}
            {signType.value === '3' && (
              <SigCreator onUpload={onUpload} pdfFile={fileTemplate} />
            )}
          </div>
        </div>
        <div className="modal-footer">
          <span className="document-sign-type">
            Выберите способ подписания документа:
          </span>
          <button className="btn" style={{ width: '20%', padding: '0' }}>
            <Select
              autosize={false}
              className={'Select'}
              name="form-field-lang"
              placeholder="Подписать другим способом"
              value={signType}
              style={{ borderColor: '#ccc' }}
              onChange={handleChange}
              options={options}
              clearable={false}
            />
          </button>
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={onCloseModal}
          >
            Отменить
          </button>
          {isLoading ? (
            <button type="button" className="btn btn-warning" disabled={true}>
              Сохранение...
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={
                signType.value === '2'
                  ? () => sendToGK()
                  : () => saveAndSendFile()
              }
            >
              {`${
                signType.value === '2' ? 'Отправить в Госключ' : 'Сохранить'
              }`}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};
